import * as React from "react";
import { useEffect, useState } from "react";
import HeaderBar from "./commons/components/HeaderBar";
import "./PatientInformation.scss";
import {
  PatientFormConfig,
  PatientInformationSection,
  PatientProfile,
  PatientProfileView,
  PatientProfileWithConfiguration,
} from "./patient-profile/PatientProfile";
import sfetch from "./commons/utils/super-fetch";
import PatientSummary from "./patient-profile/PatientSummary";
import { fillTemplateString, unescapePathOf } from "./commons/utils/text-rendering";
import * as Sentry from "@sentry/browser";
import LoadingSpinner from "./flow/utils/LoadingSpinner";
import {
  getAvailableSections,
  PatientInformationModal,
} from "./patient-portal/PatientInformationModals";
import MobileApplicationView from "./patient-portal/MobileApplicationView";
import MobileApplicationLink from "./patient-portal/MobileApplicationLink";
import { HashRouter, Route, Routes } from "react-router-dom";
import HouseholdInfoSummary from "./patient-portal/HouseholdInfoSummary";
import InsuranceInfoSummary from "./patient-portal/InsuranceInfoSummary";
import DentalInfoSummary from "./patient-portal/DentalInfoSummary";
import HealthHistoryInfoSummary from "./patient-portal/HealthHistoryInfoSummary";
import AuthorizationInfoSummary from "./patient-portal/AuthorizationInfoSummary";
import ModalPopup from "./commons/components/modal/ModalPopup";
import PersonalInfoForm from "./patient-portal/PersonalInfoForm";
import HouseholdInfoForm from "./patient-portal/HouseholdInfoForm";
import DualInsuranceInfoForm from "./patient-portal/DualInsuranceInfoForm";
import DentalInfoForm from "./patient-portal/DentalInfoForm";
import HealthHistoryInfoForm from "./patient-portal/HealthHistoryInfoForm";
import AuthorizationInfoForm from "./patient-portal/AuthorizationInfoForm";
import MultiStepForm from "./commons/components/form/MultiStepForm";
import NavigablePatientBar from "./patient-profile/NavigablePatientBar";
import Country from "./commons/model/address/Country";
import PatientCard from "./patient-profile/PatientCard";
import { getPatientLabelledData } from "./patient-profile/PatientData";
import EditAction from "./commons/components/action/EditAction";
import { ErrorCode } from "./commons/utils/errorCode";
import Error from "./commons/components/Error";
import { createRoot } from "react-dom/client";

export default function PatientInformation({
  availableCountries,
  patients,
  fetchPatientUrl,
  mobileApplicationView,
}: {
  availableCountries: Country[];
  patients: PatientSummary[];
  fetchPatientUrl: string;
  mobileApplicationView?: MobileApplicationView;
}) {
  return (
    <>
      <HeaderBar
        title={i18n["Page.Portal.PatientInformation"]}
        action={
          mobileApplicationView && (
            <MobileApplicationLink mobileApplicationView={mobileApplicationView} />
          )
        }
      />
      <div className="container">
        <div className="panel with-shadow">
          {patients.length > 1 ? (
            <HashRouter>
              <NavigablePatientBar patients={patients} />
              <Routes>
                {patients.map((patientSummary: PatientSummary, index: number) => (
                  <Route
                    path={index == 0 ? "/" : `/${patientSummary.uid}`}
                    key={patientSummary.uid}
                    element={
                      <LoadingPatientProfileViewer
                        patientUid={patientSummary.uid}
                        forMyself={index == 0}
                        availableCountries={availableCountries}
                        fetchPatientUrl={fetchPatientUrl}
                      />
                    }
                  />
                ))}
              </Routes>
            </HashRouter>
          ) : (
            <LoadingPatientProfileViewer
              patientUid={patients[0].uid}
              forMyself={true}
              availableCountries={availableCountries}
              fetchPatientUrl={fetchPatientUrl}
            />
          )}
        </div>
      </div>
    </>
  );
}

function LoadingPatientProfileViewer({
  patientUid,
  forMyself,
  availableCountries,
  fetchPatientUrl,
}: {
  patientUid: string;
  forMyself: boolean;
  availableCountries: Country[];
  fetchPatientUrl: string;
}) {
  const [patient, setPatient] = useState<PatientProfileView>(null);
  const [errorMessageCode, setErrorMessageCode] = useState<string>();

  const setCurrentPatientProfileView = (
    patientProfileWithConfiguration: PatientProfileWithConfiguration
  ) => {
    setPatient({
      patientProfile: patientProfileWithConfiguration.patientProfile,
      configuration: patientProfileWithConfiguration.configuration,
      availableSections: getAvailableSections(patientProfileWithConfiguration.configuration),
      isLinkedToADoctor: patientProfileWithConfiguration.isLinkedToADoctor,
      showLeadStatus: patientProfileWithConfiguration.showLeadStatus,
    });
  };

  useEffect(() => {
    const url = fillTemplateString(unescapePathOf(fetchPatientUrl), {
      uid: patientUid,
    });
    sfetch(url, {
      method: "GET",
    })
      .then((it) => it.json())
      .then(setCurrentPatientProfileView)
      .catch((error) => {
        error.json().then((response) => {
          if (response.code === ErrorCode.PATIENT_OR_DEPENDENT_NOT_LINKED_TO_DOCTOR) {
            setErrorMessageCode("LegalGuardian.MissingDependent.Error");
          } else {
            Sentry.captureException(error);
            setErrorMessageCode("Global.TechnicalError");
          }
        });
      });
  }, []);

  if (errorMessageCode) {
    return <Error errorCode={errorMessageCode} />;
  }

  return patient ? (
    <PatientProfileViewer
      patient={patient}
      forMyself={forMyself}
      availableCountries={availableCountries}
    />
  ) : (
    <div className={"spinner-container"}>
      <LoadingSpinner />
    </div>
  );
}

function PatientProfileViewer({
  patient,
  forMyself,
  availableCountries,
}: {
  patient: PatientProfileView;
  forMyself: boolean;
  availableCountries: Country[];
}) {
  const [showCompleteInformationForm, setShowCompleteInformationForm] = useState<boolean>(false);

  const [openedEditForm, setOpenedEditForm] = useState<PatientInformationSection>();

  const hasSection = (section: PatientInformationSection) =>
    patient.availableSections.includes(section);

  const patientProfile = patient.patientProfile;

  return (
    <>
      <div className="panel-content h-flex">
        <div className="profile-part">
          {hasSection(PatientInformationSection.PERSONAL_INFO) && (
            <>
              <div className="panel-title h-flex space-between no-wrap">
                {i18n["Page.Portal.PersonalInformation.Title"]}
                {(patientProfile.completeProfile || !patient.isLinkedToADoctor) && (
                  <EditAction
                    onClick={() => setOpenedEditForm(PatientInformationSection.PERSONAL_INFO)}
                  />
                )}
              </div>

              <PatientCard
                patient={patientProfile.personalInformation}
                legalGuardians={[]}
                patientData={getPatientLabelledData}
                isPatientView={true}
                showLeadStatus={patient.showLeadStatus}
              />
            </>
          )}
          {patient.isLinkedToADoctor &&
            patientProfile.completeProfile &&
            hasSection(PatientInformationSection.HOUSEHOLD_INFO) && (
              <HouseholdInfoSummary
                forMyself={forMyself}
                householdInfo={patientProfile.householdInformation}
                editAction={!!patientProfile.householdInformation}
                showEditHouseholdInfoForm={() =>
                  setOpenedEditForm(PatientInformationSection.HOUSEHOLD_INFO)
                }
              />
            )}
        </div>
        {patient.isLinkedToADoctor &&
          (!patientProfile.completeProfile ? (
            <div className="profile-part call-to-action h-flex centered">
              <CompleteInformationCallToAction
                forMyself={forMyself}
                setShowCompleteInformationForm={setShowCompleteInformationForm}
              />
            </div>
          ) : (
            <>
              {hasSection(PatientInformationSection.INSURANCE_INFO) && (
                <div className="profile-part">
                  <InsuranceInfoSummary
                    forMyself={forMyself}
                    primaryInsuranceInfo={patientProfile.primaryInsuranceInformation}
                    secondaryInsuranceInfo={patientProfile.secondaryInsuranceInformation}
                    editAction={!!patientProfile.primaryInsuranceInformation}
                    showEditInsuranceForm={() =>
                      setOpenedEditForm(PatientInformationSection.INSURANCE_INFO)
                    }
                  />
                </div>
              )}
              {(hasSection(PatientInformationSection.DENTAL_INFO) ||
                hasSection(PatientInformationSection.HEALTH_HISTORY_INFO) ||
                hasSection(PatientInformationSection.AUTHORIZATION_INFO)) && (
                <div className={"profile-part"}>
                  {hasSection(PatientInformationSection.DENTAL_INFO) && (
                    <DentalInfoSummary
                      forMyself={forMyself}
                      dentalInfo={patientProfile.dentalInformation}
                      editAction={!!patientProfile.dentalInformation}
                      showEditDentalInfoForm={() =>
                        setOpenedEditForm(PatientInformationSection.DENTAL_INFO)
                      }
                    />
                  )}
                  {hasSection(PatientInformationSection.HEALTH_HISTORY_INFO) && (
                    <HealthHistoryInfoSummary
                      forMyself={forMyself}
                      healthHistoryInfo={patientProfile.healthHistoryInformation}
                      editAction={!!patientProfile.healthHistoryInformation}
                      showEditHealthHistoryInfoForm={() =>
                        setOpenedEditForm(PatientInformationSection.HEALTH_HISTORY_INFO)
                      }
                    />
                  )}
                  {hasSection(PatientInformationSection.AUTHORIZATION_INFO) && (
                    <AuthorizationInfoSummary
                      forMyself={forMyself}
                      authorizationInfo={patientProfile.authorizationInformation}
                      editAction={true}
                      showEditAuthorizationForm={() =>
                        setOpenedEditForm(PatientInformationSection.AUTHORIZATION_INFO)
                      }
                    />
                  )}
                </div>
              )}
            </>
          ))}
      </div>
      {showCompleteInformationForm && (
        <ModalPopup
          closeModal={() => document.location.reload()}
          id="modal-complete-information-form"
          fullscreen={true}
        >
          <CompleteInformationForm
            configuration={patient.configuration}
            patient={patientProfile}
            myself={forMyself}
            availableCountries={availableCountries}
            defaultCountryName={patientProfile.personalInformation.country}
            availableSections={patient.availableSections}
          />
        </ModalPopup>
      )}
      <PatientInformationModal
        patientProfileWithConfiguration={patient}
        section={openedEditForm}
        availableCountries={availableCountries}
        closeModal={() => setOpenedEditForm(null)}
        forMyself={forMyself}
      />
    </>
  );
}

function CompleteInformationCallToAction({
  forMyself,
  setShowCompleteInformationForm,
}: {
  forMyself: boolean;
  setShowCompleteInformationForm: (boolean: boolean) => any;
}) {
  return (
    <div className="complete-information-call-to-action v-flex no-wrap centered">
      <svg
        width="106"
        height="120"
        viewBox="0 0 106 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g className="customized-svg">
          <path d="M105.419 34.628C105.137 33.5706 104.458 32.6862 103.51 32.1387L100.061 30.1474C99.1124 29.5999 98.0073 29.4543 96.949 29.7382C95.8915 30.0211 95.0071 30.6995 94.4597 31.6479L92.4016 35.2121C91.5281 34.8019 90.5476 34.7113 89.6037 34.964C88.5463 35.2478 87.6619 35.9262 87.1144 36.8747L86.9844 37.099V21.9141C86.9844 17.0673 83.0421 13.125 78.1953 13.125H65.3752C65.0886 11.1411 63.3821 9.60938 61.3203 9.60938H55.3438V5.27344C55.3438 2.36572 52.978 0 50.0703 0H37.1797C34.272 0 31.9062 2.36572 31.9062 5.27344V9.60938H25.9297C23.8679 9.60938 22.1605 11.1411 21.8748 13.125H9.05469C4.20789 13.125 0.265625 17.0673 0.265625 21.9141V111.211C0.265625 116.058 4.20789 120 9.05469 120H63.9094C64.8799 120 65.6672 119.214 65.6672 118.242C65.6672 117.271 64.8799 116.484 63.9094 116.484H9.05469C6.14697 116.484 3.78125 114.119 3.78125 111.211V21.9141C3.78125 19.0063 6.14697 16.6406 9.05469 16.6406H21.8281V25.1953C21.8281 27.4567 23.6683 29.2969 25.9297 29.2969H61.3203C63.5817 29.2969 65.4219 27.4567 65.4219 25.1953V23.5547H76.5547V55.1642L58.668 86.1447C58.0217 87.2635 57.7287 88.5361 57.8212 89.8251L58.6049 100.806C58.6735 101.766 59.21 102.632 60.0395 103.12C60.4973 103.39 61.0109 103.526 61.5272 103.526C61.9465 103.526 62.3668 103.435 62.7586 103.254L72.8642 98.5675C74.0745 98.0063 75.067 97.099 75.7335 95.9445L76.5547 94.5227V109.57H10.6953V23.5547H15.3828C16.3533 23.5547 17.1406 22.7682 17.1406 21.7969C17.1406 20.8255 16.3533 20.0391 15.3828 20.0391H10.1094C8.49347 20.0391 7.17969 21.3528 7.17969 22.9688V110.156C7.17969 111.772 8.49347 113.086 10.1094 113.086H77.1406C78.7556 113.086 80.0703 111.772 80.0703 110.156V88.4326L83.4688 82.5467V111.211C83.4688 114.119 81.103 116.484 78.1953 116.484H72.1116C71.1412 116.484 70.3538 117.271 70.3538 118.242C70.3538 119.213 71.1412 120 72.1116 120H78.1953C83.0412 120 86.9844 116.057 86.9844 111.211V76.4575L104.157 46.7139C104.704 45.7654 104.85 44.6603 104.566 43.6029C104.313 42.659 103.745 41.8561 102.952 41.304L105.01 37.7399C105.558 36.7914 105.703 35.6863 105.419 34.628ZM97.5038 33.4058C97.6649 33.1265 98.0229 33.0304 98.303 33.1915L101.752 35.1828C101.934 35.2881 102.001 35.451 102.025 35.538C102.048 35.6259 102.071 35.799 101.966 35.9821L99.9162 39.5316L95.4548 36.9562L97.5038 33.4058ZM90.1585 38.6325C90.2638 38.4503 90.4268 38.3835 90.5137 38.3606C90.6016 38.3359 90.7747 38.313 90.9568 38.4183L100.898 44.1577C101.081 44.263 101.148 44.425 101.17 44.5129C101.194 44.5999 101.217 44.7739 101.112 44.9561L99.6479 47.4911L88.6955 41.1676L90.1585 38.6325ZM35.4219 5.27344C35.4219 4.30389 36.2101 3.51562 37.1797 3.51562H50.0703C51.0399 3.51562 51.8281 4.30389 51.8281 5.27344V9.60938H35.4219V5.27344ZM61.9062 25.1953C61.9062 25.5185 61.6435 25.7812 61.3203 25.7812H25.9297C25.6065 25.7812 25.3438 25.5185 25.3438 25.1953V13.7109C25.3438 13.3878 25.6065 13.125 25.9297 13.125H61.3203C61.6435 13.125 61.9062 13.3878 61.9062 13.7109V25.1953ZM77.1406 20.0391H65.4219V16.6406H78.1953C81.103 16.6406 83.4688 19.0063 83.4688 21.9141V43.1891L80.0703 49.075V22.9688C80.0703 21.3528 78.7565 20.0391 77.1406 20.0391ZM86.9377 44.2126L97.8901 50.5362L76.3405 87.8622L65.3871 81.5387L86.9377 44.2126ZM72.6893 94.1867C72.3863 94.7113 71.9349 95.1233 71.3856 95.3778V95.3787L62.0509 99.7073L61.3276 89.5752C61.2855 88.9893 61.4192 88.4116 61.7131 87.9034L63.6293 84.5828L74.5826 90.9064L72.6893 94.1867Z" />
          <path d="M18.4224 98.0713C18.7565 98.3688 19.174 98.5144 19.5887 98.5144C20.074 98.5144 20.5565 98.3148 20.9034 97.9239L24.3889 93.9999L27.0869 97.218C27.628 97.8644 28.4218 98.2452 29.264 98.2654C30.1072 98.28 30.9166 97.9413 31.4869 97.3215L34.7316 93.7985L38.3278 97.869C38.9714 98.5968 40.0819 98.6655 40.8088 98.0228C41.5367 97.3801 41.6053 96.2695 40.9626 95.5417L36.9362 90.9842C36.3878 90.3635 35.5986 90.0027 34.7709 89.9945C34.7609 89.9936 34.7508 89.9936 34.7407 89.9936C33.9232 89.9936 33.1395 90.3369 32.5856 90.9384L29.3528 94.4485L26.6465 91.2213C26.0991 90.5685 25.2961 90.1868 24.4429 90.174C23.5896 90.1547 22.7775 90.5209 22.2117 91.1581L18.2759 95.5893C17.6304 96.3153 17.6964 97.4268 18.4224 98.0713Z" />
          <path d="M70.2852 38.7891C70.2852 37.8177 69.4978 37.0312 68.5273 37.0312H18.6055C17.635 37.0312 16.8477 37.8177 16.8477 38.7891C16.8477 39.7604 17.635 40.5469 18.6055 40.5469H68.5273C69.4978 40.5469 70.2852 39.7604 70.2852 38.7891Z" />
          <path d="M18.8398 49.9219H39.6992C40.6697 49.9219 41.457 49.1354 41.457 48.1641C41.457 47.1927 40.6697 46.4062 39.6992 46.4062H18.8398C17.8694 46.4062 17.082 47.1927 17.082 48.1641C17.082 49.1354 17.8694 49.9219 18.8398 49.9219Z" />
          <path d="M57.8633 48.1641C57.8633 47.1927 57.0759 46.4062 56.1055 46.4062H47.9023C46.9319 46.4062 46.1445 47.1927 46.1445 48.1641C46.1445 49.1354 46.9319 49.9219 47.9023 49.9219H56.1055C57.0759 49.9219 57.8633 49.1354 57.8633 48.1641Z" />
          <path d="M18.8398 59.2969H35.7148C36.6853 59.2969 37.4727 58.5104 37.4727 57.5391C37.4727 56.5677 36.6853 55.7812 35.7148 55.7812H18.8398C17.8694 55.7812 17.082 56.5677 17.082 57.5391C17.082 58.5104 17.8694 59.2969 18.8398 59.2969Z" />
          <path d="M51.1836 55.7812H43.2148C42.2444 55.7812 41.457 56.5677 41.457 57.5391C41.457 58.5104 42.2444 59.2969 43.2148 59.2969H51.1836C52.1541 59.2969 52.9414 58.5104 52.9414 57.5391C52.9414 56.5677 52.1541 55.7812 51.1836 55.7812Z" />
          <path d="M18.8398 68.6719H31.0273C31.9978 68.6719 32.7852 67.8854 32.7852 66.9141C32.7852 65.9427 31.9978 65.1562 31.0273 65.1562H18.8398C17.8694 65.1562 17.082 65.9427 17.082 66.9141C17.082 67.8854 17.8694 68.6719 18.8398 68.6719Z" />
          <path d="M52.9414 66.9141C52.9414 65.9427 52.1541 65.1562 51.1836 65.1562H38.5273C37.5569 65.1562 36.7695 65.9427 36.7695 66.9141C36.7695 67.8854 37.5569 68.6719 38.5273 68.6719H51.1836C52.1541 68.6719 52.9414 67.8854 52.9414 66.9141Z" />
          <path d="M18.8398 78.0469H31.0273C31.9978 78.0469 32.7852 77.2604 32.7852 76.2891C32.7852 75.3177 31.9978 74.5312 31.0273 74.5312H18.8398C17.8694 74.5312 17.082 75.3177 17.082 76.2891C17.082 77.2604 17.8694 78.0469 18.8398 78.0469Z" />
        </g>
      </svg>
      <div className="instructions">
        {
          i18n[
            forMyself
              ? "Page.Portal.PatientInformation.CompleteInformation.Instruction.ForMySelf"
              : "Page.Portal.PatientInformation.CompleteInformation.Instruction.ForOther"
          ]
        }
      </div>
      <button
        id="complete-information-button"
        className="button-secondary small"
        onClick={() => setShowCompleteInformationForm(true)}
      >
        {
          i18n[
            forMyself
              ? "Page.Portal.PatientInformation.CompleteInformation.ForMyself.Button"
              : "Page.Portal.PatientInformation.CompleteInformation.Button"
          ]
        }
      </button>
    </div>
  );
}

function CompleteInformationForm({
  patient,
  myself,
  availableCountries,
  defaultCountryName,
  configuration,
  availableSections,
}: {
  configuration: PatientFormConfig;
  patient: PatientProfile;
  myself: boolean;
  availableCountries: Country[];
  defaultCountryName?: string;
  availableSections: PatientInformationSection[];
}) {
  const steps = getAvailableSections(configuration);

  const [currentStep, setCurrentStep] = useState<PatientInformationSection>(steps[0]);

  const nextStep = () => {
    const nextStepIndex = steps.indexOf(currentStep) + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(steps[nextStepIndex]);
    } else {
      window.location.reload();
    }
  };

  const currentStepIndex = steps.indexOf(currentStep);
  const submitLabel =
    currentStepIndex < steps.length - 1 ? i18n["Global.SaveAndNext"] : i18n["Global.Save"];

  const composeTitle = (selfTitle: string, otherTitleTemplate: string) => {
    if (myself) return selfTitle;
    else
      return fillTemplateString(otherTitleTemplate, {
        firstname: patient.personalInformation.firstName,
      });
  };

  const getForm = (section: PatientInformationSection) => {
    switch (section) {
      case PatientInformationSection.PERSONAL_INFO:
        return (
          <PersonalInfoForm
            {...configuration.personalInfo}
            patientInformation={patient.personalInformation}
            availableCountries={availableCountries}
            title={composeTitle(
              i18n["Form.PersonalInfo.Title.ForMySelf"],
              i18n["Form.PersonalInfo.Title.ForOther"]
            )}
            submitLabel={submitLabel}
            afterSubmit={nextStep}
          />
        );
      case PatientInformationSection.HOUSEHOLD_INFO:
        return (
          <HouseholdInfoForm
            {...configuration.householdInfo}
            householdInfo={patient.householdInformation}
            afterSubmit={nextStep}
            submitLabel={submitLabel}
            title={composeTitle(
              i18n["Form.Household.Title.ForMySelf"],
              i18n["Form.Household.Title.ForOther"]
            )}
            skippable={true}
          />
        );
      case PatientInformationSection.INSURANCE_INFO:
        return (
          <DualInsuranceInfoForm
            {...configuration.insuranceInfo}
            primaryInsuranceInfo={patient.primaryInsuranceInformation}
            secondaryInsuranceInfo={patient.secondaryInsuranceInformation}
            availableCountries={availableCountries}
            defaultCountry={defaultCountryName}
            afterSubmit={nextStep}
            title={composeTitle(
              i18n["Form.InsuranceInfo.Title.ForMySelf"],
              i18n["Form.InsuranceInfo.Title.ForOther"]
            )}
            submitLabel={submitLabel}
          />
        );
      case PatientInformationSection.DENTAL_INFO:
        return (
          <DentalInfoForm
            {...configuration.dentalInfo}
            dentalInfo={patient.dentalInformation}
            afterSubmit={nextStep}
            title={composeTitle(
              i18n["Form.DentalInformation.Title.ForMySelf"],
              i18n["Form.DentalInformation.Title.ForOther"]
            )}
            submitLabel={submitLabel}
          />
        );
      case PatientInformationSection.HEALTH_HISTORY_INFO:
        return (
          <HealthHistoryInfoForm
            {...configuration.healthHistoryInfo}
            healthHistoryInfo={patient.healthHistoryInformation}
            afterSubmit={nextStep}
            title={composeTitle(
              i18n["Form.HealthHistoryInformation.Title.ForMySelf"],
              i18n["Form.HealthHistoryInformation.Title.ForOther"]
            )}
            submitLabel={submitLabel}
          />
        );
      case PatientInformationSection.AUTHORIZATION_INFO:
        return (
          <AuthorizationInfoForm
            {...configuration.authorizationInfo}
            authorizationInfo={patient.authorizationInformation}
            submitLabel={submitLabel}
            afterSubmit={nextStep}
            title={i18n["Form.Authorization.Title"]}
          />
        );
    }
  };

  return (
    <MultiStepForm currentStep={currentStepIndex}>
      {availableSections.map((section) => getForm(section))}
    </MultiStepForm>
  );
}

PatientInformation.loadComponent = (elementId: string, props) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<PatientInformation {...props} />);
};
